import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  AlertTriangle,
  Clock,
  DollarSign,
  Calendar,
  Eye,
  Send,
  Plus,
  TrendingUp,
  Download,
  Filter,
  Search,
  Bell,
  FileText
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const OverdueInvoicesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDays, setFilterDays] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState('all');

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    setTimeout(() => {
      const mockInvoices = [
        {
          id: 'INV-2024-003',
          clientName: 'فاطمة علي',
          clientType: 'فرد',
          dueDate: '2024-11-30',
          daysOverdue: 35,
          remainingAmount: 8000,
          status: 'overdue',
          totalAmount: 8000,
          severity: 'high' // high, medium, low
        },
        {
          id: 'INV-2024-007',
          clientName: 'شركة التقنية المتقدمة',
          clientType: 'شركة',
          dueDate: '2024-12-15',
          daysOverdue: 20,
          remainingAmount: 25000,
          status: 'overdue',
          totalAmount: 25000,
          severity: 'medium'
        },
        {
          id: 'INV-2024-012',
          clientName: 'محمد سالم',
          clientType: 'فرد',
          dueDate: '2024-12-28',
          daysOverdue: 7,
          remainingAmount: 3500,
          status: 'overdue',
          totalAmount: 3500,
          severity: 'low'
        },
        {
          id: 'INV-2024-015',
          clientName: 'شركة النور التجارية',
          clientType: 'شركة',
          dueDate: '2024-11-20',
          daysOverdue: 45,
          remainingAmount: 15000,
          status: 'overdue',
          totalAmount: 15000,
          severity: 'high'
        },
        {
          id: 'INV-2024-018',
          clientName: 'عبدالله أحمد',
          clientType: 'فرد',
          dueDate: '2024-12-10',
          daysOverdue: 25,
          remainingAmount: 6000,
          status: 'overdue',
          totalAmount: 6000,
          severity: 'medium'
        },
        {
          id: 'INV-2024-020',
          clientName: 'سارة محمود',
          clientType: 'فرد',
          dueDate: '2024-12-30',
          daysOverdue: 5,
          remainingAmount: 2000,
          status: 'overdue',
          totalAmount: 2000,
          severity: 'low'
        }
      ];
      setInvoices(mockInvoices);
      setLoading(false);
    }, 500);
  }, []);

  // Calculate stats
  const stats = {
    totalOverdue: invoices.length,
    totalAmount: invoices.reduce((sum, inv) => sum + inv.remainingAmount, 0),
    oldestInvoice: Math.max(...invoices.map(inv => inv.daysOverdue), 0),
    averageDays: invoices.length > 0 
      ? Math.round(invoices.reduce((sum, inv) => sum + inv.daysOverdue, 0) / invoices.length)
      : 0
  };

  // Get severity badge
  const getSeverityBadge = (daysOverdue) => {
    if (daysOverdue < 7) {
      return {
        label: `${daysOverdue} أيام`,
        className: 'bg-yellow-50 text-yellow-700 border-yellow-300',
        icon: Clock
      };
    } else if (daysOverdue <= 30) {
      return {
        label: `${daysOverdue} يوم`,
        className: 'bg-orange-50 text-orange-700 border-orange-300',
        icon: AlertTriangle
      };
    } else {
      return {
        label: `${daysOverdue} يوم`,
        className: 'bg-red-50 text-red-700 border-red-300',
        icon: AlertTriangle
      };
    }
  };

  // Get row background based on severity
  const getRowClass = (daysOverdue) => {
    if (daysOverdue < 7) {
      return 'bg-yellow-50/30 hover:bg-yellow-50/50';
    } else if (daysOverdue <= 30) {
      return 'bg-orange-50/30 hover:bg-orange-50/50';
    } else {
      return 'bg-red-50/30 hover:bg-red-50/50';
    }
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDays = 
      filterDays === 'all' ||
      (filterDays === 'low' && invoice.daysOverdue < 7) ||
      (filterDays === 'medium' && invoice.daysOverdue >= 7 && invoice.daysOverdue <= 30) ||
      (filterDays === 'high' && invoice.daysOverdue > 30);

    return matchesSearch && matchesDays;
  });

  // Handle actions
  const handleSendReminder = (invoiceId) => {
    if (window.confirm('هل تريد إرسال تذكير للعميل؟')) {
      alert(`تم إرسال تذكير للفاتورة ${invoiceId}`);
    }
  };

  const handleRecordPayment = (invoiceId) => {
    navigate(`/admin/finance/invoices/${invoiceId}/payment`);
  };

  const handleEscalate = (invoiceId) => {
    if (window.confirm('هل تريد تحويل هذه الفاتورة لقسم التحصيل؟')) {
      alert(`تم تحويل الفاتورة ${invoiceId} لقسم التحصيل`);
    }
  };

  const handleExport = () => {
    alert('سيتم تصدير التقرير قريباً');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل الفواتير المتراجعة...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/invoices')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للفواتير</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-600" />
                <span>الفواتير المتراجعة</span>
              </h1>
              <p className="text-gray-600">إدارة ومتابعة الفواتير غير المحصلة</p>
            </div>

            {hasPermission('finance.export') && (
              <button
                onClick={handleExport}
                className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
              >
                <Download className="w-5 h-5" />
                <span>تصدير التقرير</span>
              </button>
            )}
          </div>
        </div>

        {/* 1️⃣ Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">عدد الفواتير المتأخرة</p>
                <p className="text-3xl font-black text-red-600">{stats.totalOverdue}</p>
              </div>
              <AlertTriangle className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي المبالغ المتأخرة</p>
                <p className="text-2xl font-black text-red-600">{stats.totalAmount.toLocaleString()} ر.س</p>
              </div>
              <DollarSign className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-orange-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">أقدم فاتورة</p>
                <p className="text-3xl font-black text-orange-600">{stats.oldestInvoice} يوم</p>
              </div>
              <Calendar className="w-12 h-12 text-orange-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">متوسط مدة التأخير</p>
                <p className="text-3xl font-black text-yellow-600">{stats.averageDays} يوم</p>
              </div>
              <TrendingUp className="w-12 h-12 text-yellow-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* 4️⃣ Visual Alert Panel */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-black text-gray-800 mb-4">مستويات الخطورة</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-6 h-6 text-yellow-600" />
                <span className="font-bold text-yellow-800">أقل من 7 أيام</span>
              </div>
              <p className="text-sm text-yellow-700">تحذير أولي - متابعة عادية</p>
              <p className="text-2xl font-black text-yellow-600 mt-2">
                {invoices.filter(i => i.daysOverdue < 7).length} فاتورة
              </p>
            </div>

            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                <span className="font-bold text-orange-800">7 - 30 يوم</span>
              </div>
              <p className="text-sm text-orange-700">تحذير متوسط - متابعة مكثفة</p>
              <p className="text-2xl font-black text-orange-600 mt-2">
                {invoices.filter(i => i.daysOverdue >= 7 && i.daysOverdue <= 30).length} فاتورة
              </p>
            </div>

            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <span className="font-bold text-red-800">أكثر من 30 يوم</span>
              </div>
              <p className="text-sm text-red-700">خطر عالي - إجراءات عاجلة</p>
              <p className="text-2xl font-black text-red-600 mt-2">
                {invoices.filter(i => i.daysOverdue > 30).length} فاتورة
              </p>
            </div>
          </div>
        </div>

        {/* 3️⃣ Smart Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث برقم الفاتورة أو اسم العميل..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              />
            </div>

            {/* Days Filter */}
            <div className="relative">
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterDays}
                onChange={(e) => setFilterDays(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الفواتير</option>
                <option value="low">أقل من 7 أيام</option>
                <option value="medium">7 - 30 يوم</option>
                <option value="high">أكثر من 30 يوم</option>
              </select>
            </div>

            {/* Date Range Filter */}
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterDateRange}
                onChange={(e) => setFilterDateRange(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع التواريخ</option>
                <option value="this-week">هذا الأسبوع</option>
                <option value="this-month">هذا الشهر</option>
                <option value="last-month">الشهر الماضي</option>
                <option value="older">أقدم من شهر</option>
              </select>
            </div>
          </div>
        </div>

        {/* 2️⃣ Overdue Invoices Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">رقم الفاتورة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">العميل</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ الاستحقاق</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">عدد أيام التأخير</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المبلغ المتبقي</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">إجراءات سريعة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>لا توجد فواتير متراجعة</p>
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => {
                    const severityBadge = getSeverityBadge(invoice.daysOverdue);
                    const Icon = severityBadge.icon;

                    return (
                      <tr key={invoice.id} className={`transition-colors ${getRowClass(invoice.daysOverdue)}`}>
                        <td className="px-4 py-3">
                          <span className="font-bold text-[#1E3A8A]">{invoice.id}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-semibold text-gray-800">{invoice.clientName}</p>
                            <p className="text-xs text-gray-500">{invoice.clientType}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{invoice.dueDate}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold border-2 ${severityBadge.className}`}>
                              <Icon className="w-4 h-4" />
                              {severityBadge.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-red-600 text-lg">{invoice.remainingAmount.toLocaleString()} ر.س</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border bg-red-50 text-red-700 border-red-200">
                              <AlertTriangle className="w-3 h-3" />
                              متأخرة
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            {/* View */}
                            <button
                              onClick={() => navigate(`/admin/finance/invoices/${invoice.id}`)}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                              title="عرض التفاصيل"
                            >
                              <Eye className="w-4 h-4 text-blue-600" />
                            </button>

                            {/* Record Payment */}
                            {hasPermission('finance.payment') && (
                              <button
                                onClick={() => handleRecordPayment(invoice.id)}
                                className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                                title="تسجيل دفعة"
                              >
                                <Plus className="w-4 h-4 text-green-600" />
                              </button>
                            )}

                            {/* Send Reminder */}
                            {hasPermission('finance.notify') && (
                              <button
                                onClick={() => handleSendReminder(invoice.id)}
                                className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                                title="إرسال تذكير"
                              >
                                <Send className="w-4 h-4 text-yellow-600" />
                              </button>
                            )}

                            {/* Escalate to Collections */}
                            {hasPermission('finance.collections') && invoice.daysOverdue > 30 && (
                              <button
                                onClick={() => handleEscalate(invoice.id)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                title="تحويل للتحصيل"
                              >
                                <Bell className="w-4 h-4 text-red-600" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Footer */}
        {filteredInvoices.length > 0 && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">عرض {filteredInvoices.length} من أصل {invoices.length} فاتورة</span>
              <span className="font-bold text-[#1E3A8A]">
                إجمالي المبالغ المعروضة: {filteredInvoices.reduce((sum, inv) => sum + inv.remainingAmount, 0).toLocaleString()} ر.س
              </span>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default OverdueInvoicesPage;
